import React from "react";

// Article 1 — Deranged Doppler in Fetal Growth Restriction: What Happens Next?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

export function DerangedDopplerFGR() {
  return (
    <article className="mt-10">
      <P>
        A “deranged Doppler” does <strong>not</strong> automatically mean that the baby must be delivered immediately. The decision depends on <strong>which vessel is abnormal, how severe the change is, gestational age, fetal growth, heart-rate monitoring and the mother’s condition</strong>.
      </P>
      <P>
        The useful question is not merely, “Is the Doppler abnormal?” It is: <strong>Which Doppler—and what does the complete picture show?</strong>
      </P>

      <H2>Doppler Does Not Directly Measure Oxygen</H2>
      <P>
        Doppler ultrasound measures blood-flow patterns. It helps the obstetrician assess placental resistance and how the baby is adapting; it does not directly measure oxygen in the baby’s blood.
      </P>
      <P>
        A small baby is also not always growth restricted. Some babies are constitutionally small and healthy. Fetal growth restriction—previously called IUGR—means that disease, commonly placental dysfunction, is preventing the baby from reaching its expected growth potential. Size, growth velocity and Doppler must therefore be interpreted together. <Cite n="1,2" />
      </P>

      <H2>Umbilical Artery: The Placental Signal</H2>
      <P>
        The umbilical artery is the main starting point in placental FGR. A report may show raised resistance, reduced flow between heartbeats, absent end-diastolic flow or reversed end-diastolic flow.
      </P>
      <P>
        Raised resistance means blood still moves forward, but against greater placental resistance.
      </P>
      <P>
        <strong>Absent end-diastolic flow</strong> means forward flow stops between some heartbeats.
      </P>
      <P>
        <strong>Reversed end-diastolic flow</strong> means it briefly moves in the wrong direction and is a more severe sign of placental dysfunction.
      </P>
      <P>These findings are a spectrum; they are not interchangeable.</P>

      <H2>Middle Cerebral Artery: “Brain-Sparing” Is Compensation</H2>
      <P>
        When placental supply is limited, the baby may redirect more blood towards the brain. This can alter the middle cerebral artery and the cerebroplacental ratio—often called <strong>brain-sparing</strong>.
      </P>
      <P>
        The term sounds reassuring, but it describes compensation, not recovery. It does not prove that the baby can safely remain inside for an unlimited time.
      </P>
      <P>
        Cerebral Doppler may add useful information, particularly in late-onset FGR. Guidelines differ, however: ISUOG uses middle cerebral artery and cerebroplacental-ratio assessment more actively, while SMFM does not recommend them for routine management of every FGR pregnancy. <Cite n="1,2" /> One cerebral Doppler value should never overrule the complete assessment.
      </P>

      <H2>Ductus Venosus: A Later Warning in Severe Early FGR</H2>
      <P>
        The ductus venosus directs oxygen-rich placental blood towards the fetal heart. In very preterm, severe early-onset FGR, an abnormal waveform may indicate increasing cardiovascular strain.
      </P>
      <P>
        Specialist units may combine ductus venosus findings with computerised cardiotocography and other tests when deciding how long pregnancy can safely continue. The TRUFFLE trial supports this multimodal approach in carefully selected very preterm pregnancies. <Cite n="3" />
      </P>
      <P>It is not a stand-alone delivery countdown.</P>

      <H2>What Happens After an Abnormal Doppler?</H2>
      <P>
        The team should review gestational dating, estimated fetal weight, abdominal circumference, growth trend, amniotic fluid, fetal movements, heart-rate monitoring, maternal blood pressure, preeclampsia symptoms and the possible cause of early FGR.
      </P>
      <P>
        Monitoring becomes more frequent as the Doppler worsens. SMFM recommends umbilical artery assessment every one to two weeks after FGR is diagnosed, weekly assessment when resistance is raised or FGR is severe, and up to two or three times weekly with absent end-diastolic flow. Reversed flow usually requires hospitalisation, corticosteroids, intensive surveillance and active consideration of delivery. <Cite n="2" />
      </P>
      <P>
        Raised resistance with forward flow may allow pregnancy to continue under close monitoring. Absent or reversed flow is more serious, but delivery must balance worsening placental function against the consequences of very premature birth.
      </P>
      <P>
        SMFM recommends delivery around 33–34 weeks with absent flow and around 30–32 weeks with reversed flow, while allowing maternal illness, fetal-heart monitoring, ductus venosus findings and local neonatal capability to change that timing. <Cite n="2" />
      </P>
      <P>
        Delivery may be earlier with severe preeclampsia, HELLP syndrome, significant bleeding, maternal deterioration or abnormal fetal monitoring. If preterm birth is likely, corticosteroids may support lung maturation and magnesium sulfate may be used for neuroprotection at the appropriate gestation. <Cite n="2" />
      </P>

      <H2>What Cannot Correct a Deranged Doppler?</H2>
      <P>
        There is no proven food, supplement or bed-rest plan that reverses established placental resistance.
      </P>
      <P>
        Do not start heparin solely to treat FGR, sildenafil, strict bed rest, protein products marketed to increase fetal blood flow, or oxygen without another clinical indication. SMFM recommends against low-molecular-weight heparin solely to prevent recurrent FGR, sildenafil for treatment and activity restriction as therapy. <Cite n="2" />
      </P>
      <P>
        Aspirin may help prevent placental disease in selected high-risk women when started at the correct time. It is not a rescue treatment after severe Doppler changes have appeared.
      </P>

      <H2>Seek Urgent Assessment</H2>
      <P>
        Contact maternity services promptly for reduced fetal movements, severe headache, visual disturbance, upper-abdominal pain, vaginal bleeding, fluid leakage, regular contractions or feeling seriously unwell. Do not wait for the next scheduled Doppler when symptoms change.
      </P>

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed">
          <strong>Dr Tania’s takeaway:</strong> A deranged Doppler is not one diagnosis and not one delivery date. Umbilical artery resistance reflects placental difficulty, cerebral redistribution reflects fetal adaptation, and ductus venosus change may reflect later cardiovascular strain. The safest decision comes from the trend, gestational age, fetal monitoring and maternal condition—not one coloured waveform.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Lees CC, Stampalija T, Baschat AA, et al. ISUOG Practice Guidelines: diagnosis and management of small-for-gestational-age fetus and fetal growth restriction. Ultrasound in Obstetrics &amp; Gynecology. 2020;56:298–312.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.isuog.org/resource/isuog-practice-guidelines-diagnosis-and-management-of-sga-and-fgr.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.isuog.org/resource/isuog-practice-guidelines-diagnosis-and-management-of-sga-and-fgr.html</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] Society for Maternal-Fetal Medicine. Consult Series #52: Diagnosis and management of fetal growth restriction. 2020; reaffirmed 2024.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://publications.smfm.org/publications/289-society-for-maternal-fetal-medicine-consult-series-52/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://publications.smfm.org/publications/289-society-for-maternal-fetal-medicine-consult-series-52/</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] Lees CC, Marlow N, van Wassenaer-Leemhuis A, et al. Two-year neurodevelopmental and intermediate perinatal outcomes in infants with very preterm fetal growth restriction: the TRUFFLE randomised trial. The Lancet. 2015;385:2162–2172.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pubmed.ncbi.nlm.nih.gov/25747582/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pubmed.ncbi.nlm.nih.gov/25747582/</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] Royal College of Obstetricians and Gynaecologists. Small-for-Gestational-Age Fetus and a Growth Restricted Fetus: Investigation and Care. Green-top Guideline No. 31. Third edition; reviewed 2024 and updated online July 2026.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.rcog.org.uk/guidance/browse-all-guidance/green-top-guidelines/small-for-gestational-age-fetus-and-a-growth-restricted-fetus-investigation-and-care-green-top-guideline-no-31/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.rcog.org.uk/guidance/browse-all-guidance/green-top-guidelines/small-for-gestational-age-fetus-and-a-growth-restricted-fetus-investigation-and-care-green-top-guideline-no-31/</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific assessment. Doppler findings must be interpreted by the treating obstetric team using gestational age, fetal growth, maternal health, fetal monitoring and local neonatal resources. Seek urgent maternity care for reduced fetal movements or any concerning symptoms.
      </P>
    </article>
  );
}
